import React, { FC, useState } from "react";
import { connect } from "react-redux";
import ChooseVerificationMethod from "./ChooseVerificationMethod";
import Pin from "./Pin";
import { VERIFICATION_TARGET, VerificationTarget } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { isTransfer as isTransferSelector, isEsim as isEsimSelector } from "../../../../app/state/selectors";
import {
    VERIFICATION_DELIVERY_METHOD,
    VERIFICATION_STATE,
    VerificationState,
    VerificationDeliveryMethod,
} from "../../../consts";
import * as CUSTOMER_INFO_SELECTORS from "../../../state/selectors/customerInfoSelectors";
import {
    getLegalSubscriptionId,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
} from "../../../state/selectors/subscriptionsSelectors";
import * as VERIFICATION_SELECTORS from "../../../state/selectors/verificationSelectors";
import "./Verification.less";

interface StateProps {
    isEsim: boolean;
    isExistingMissingEmail: boolean;
    isExistingPrepaidKBDSubscription: boolean;
    isProductUserVerified: boolean;
    isSubscriptionBroadband: boolean;
    isTransfer: boolean;
    maskedEmail: string;
}

interface VerificationProps extends StateProps {
    number: string;
    sendCode: (number: string, method: VerificationDeliveryMethod) => Promise<void>;
    verifyCode: (code: string, number: string) => Promise<void>;
}

const Verification: FC<VerificationProps> = ({
    isProductUserVerified,
    isSubscriptionBroadband,
    isExistingPrepaidKBDSubscription,
    isTransfer,
    isEsim,
    maskedEmail,
    isExistingMissingEmail,
    number,
    sendCode,
    verifyCode,
}) => {
    const [verificationState, setVerificationState] = useState<VerificationState>(VERIFICATION_STATE.INITIAL);
    const [method, setMethod] = useState<VerificationDeliveryMethod>(
        isSubscriptionBroadband ? VERIFICATION_DELIVERY_METHOD.EMAIL : VERIFICATION_DELIVERY_METHOD.SMS,
    );
    const target: VerificationTarget = isEsim
        ? VERIFICATION_TARGET.ESIM_INSTALLMENT_B2B
        : isTransfer
          ? VERIFICATION_TARGET.TRANSFER_OWNERSHIP_TO_B2C
          : VERIFICATION_TARGET.FORMER_LEGAL_OWNER_CONSENT_TRANSFER_B2B;
    const onSendOrResendPinClick = () => {
        sendCode(number, method);
    };

    const onCodeConfirm = (code: string) => {
        verifyCode(code, number);
    };

    // Special case - If user already has verified and refresh/comes back to page, show SMS state
    if (isProductUserVerified) {
        return <Pin method={method} onWithOrResendPinClick={onSendOrResendPinClick} onCodeConfirm={onCodeConfirm} />;
    }

    switch (verificationState) {
        case VERIFICATION_STATE.CODE_SENT:
            return (
                <Pin method={method} onWithOrResendPinClick={onSendOrResendPinClick} onCodeConfirm={onCodeConfirm} />
            );

        case VERIFICATION_STATE.INITIAL:
        default:
            return (
                <ChooseVerificationMethod
                    method={method}
                    target={target}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    isExistingPrepaidKBDSubscription={isExistingPrepaidKBDSubscription}
                    supportsEmailVerification={!isTransfer && !isEsim}
                    maskedEmail={maskedEmail}
                    isExistingMissingEmail={isExistingMissingEmail}
                    onSendCode={() => {
                        onSendOrResendPinClick();
                        setVerificationState(VERIFICATION_STATE.CODE_SENT);
                    }}
                    onChangeVerificationMethod={() => {
                        setMethod(method);
                    }}
                />
            );
    }
};

const mapStateToProps = (state: RootState, ownProps: { subscriptionId?: string }): StateProps => {
    const subscriptionId = ownProps.subscriptionId ?? getLegalSubscriptionId(state);

    return {
        isTransfer: isTransferSelector(state),
        isEsim: isEsimSelector(state),
        isProductUserVerified: VERIFICATION_SELECTORS.isProductUserVerified(state),
        isExistingPrepaidKBDSubscription: CUSTOMER_INFO_SELECTORS.getCurrentIsExistingPrepaidKBDSubscription(state),
        isSubscriptionBroadband: isSubscriptionBroadbandSelector(state, subscriptionId),
        maskedEmail: CUSTOMER_INFO_SELECTORS.getMaskedEmail(state),
        isExistingMissingEmail: CUSTOMER_INFO_SELECTORS.getCurrentIsExistingMissingEmail(state),
    };
};

export default connect(mapStateToProps)(Verification);
